import type { AssemblyMateDefinition } from '../../assembly'
import { resolveAssemblyMateComponentInstanceIds } from '../../assembly/utils'
import type { PuppyAssemblyDocument } from './types'
import { findPuppyMateDefinitionReferenceGraphError } from './validatePuppyPackageGraph'

/**
 * 清理历史离线包中由 occurrence 迁移遗留的重复 Mate。
 *
 * 设计约束：
 * 1. `mateId` 是业务 Mate 的稳定身份，路径迁移前后的副本会共享该值；
 * 2. 只有包内装配图能证明同组恰好一个候选完全有效时才允许自愈；
 * 3. 多个有效候选代表真实歧义，禁止按数组顺序、显示名称或时间戳猜测；
 * 4. 没有有效候选时保留原数据，让后续严格图校验输出原始未知引用错误。
 *
 * @param assembly 已完成基础结构与 Mate topology occurrence 归一化的装配文档。
 * @returns Mate 稳定身份已确定性整理的装配文档。
 */
export function normalizePuppyAssemblyDuplicateMates(
  assembly: PuppyAssemblyDocument,
): PuppyAssemblyDocument {
  const matesById = new Map<string, AssemblyMateDefinition[]>()
  assembly.mates.mates.forEach((mate) => {
    const group = matesById.get(mate.mateId) ?? []
    group.push(mate)
    matesById.set(mate.mateId, group)
  })

  const visitedMateIds = new Set<string>()
  const retainedMates: AssemblyMateDefinition[] = []
  let changed = false
  assembly.mates.mates.forEach((mate) => {
    if (visitedMateIds.has(mate.mateId)) {
      return
    }
    visitedMateIds.add(mate.mateId)
    const candidates = matesById.get(mate.mateId) ?? [mate]
    if (candidates.length === 1) {
      retainedMates.push(mate)
      return
    }

    const validCandidates = candidates.filter((candidate) => (
      findPuppyMateDefinitionReferenceGraphError(assembly, candidate) === null
    ))
    if (validCandidates.length > 1) {
      throw new Error(`离线包重复 Mate 身份存在多个有效候选：${mate.mateId}`)
    }
    if (validCandidates.length === 1) {
      retainedMates.push(validCandidates[0]!)
      changed = true
      return
    }

    // 全部候选都失效时不能擅自选取；保留原组并交给严格图校验报告具体悬挂引用。
    retainedMates.push(...candidates)
  })

  if (!changed) {
    return assembly
  }

  const retainedComponentIds = new Set(
    retainedMates.flatMap((mate) => resolveAssemblyMateComponentInstanceIds(mate)),
  )
  const components = Object.fromEntries(
    Object.values(assembly.mates.components)
      .filter((component) => retainedComponentIds.has(component.componentInstanceId))
      .map((component) => [component.componentInstanceId, component]),
  )

  return {
    ...assembly,
    mates: {
      ...assembly.mates,
      components,
      mates: retainedMates,
    },
  }
}
